"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";
import type { Id } from "../../../../convex/_generated/dataModel";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MessageBubble,
  MemberRow,
  BannedMemberRow,
} from "@/components/org/OrganizationComponents";
import type { MemberManageActions } from "@/components/org/OrganizationComponents";
import { OrganizationHeader } from "@/components/org/OrganizationHeader";
import {
  SendIcon,
  ChatIcon,
  UsersIcon,
  BanIcon,
  XIcon,
  ArrowLeftIcon,
} from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export default function OrganizationPage() {
  const params = useParams();
  const slug = params.slug as string;

  const org = useQuery(api.organizations.getBySlug, { slug });
  const currentUser = useQuery(api.users.getCurrentUser);
  const members = useQuery(
    api.members.getByOrganization,
    org ? { organizationId: org._id } : "skip"
  );
  const onlineCount = useQuery(
    api.members.getOnlineCount,
    org ? { organizationId: org._id } : "skip"
  );
  const linkedAccount = useQuery(
    api.linkedAccounts.getCurrentForOrganization,
    org ? { organizationId: org._id } : "skip"
  );
  const messages = useQuery(
    api.messages.getByOrganization,
    org ? { organizationId: org._id, limit: 100 } : "skip"
  );
  const membership = useQuery(
    api.members.getCurrentMembership,
    org ? { organizationId: org._id } : "skip"
  );

  const bannedMembers = useQuery(
    api.members.getBannedByOrganization,
    org && membership && ["owner", "admin"].includes(membership.role)
      ? { organizationId: org._id }
      : "skip"
  );

  const sendMessage = useMutationWithAuthRedirect(api.messages.send);
  const removeMessage = useMutationWithAuthRedirect(api.messages.remove);
  const linkAccount = useMutationWithAuthRedirect(api.linkedAccounts.linkFromCode);
  const joinOrg = useMutationWithAuthRedirect(api.members.join);
  const updateRole = useMutationWithAuthRedirect(api.members.updateRole);
  const kickMember = useMutationWithAuthRedirect(api.members.kick);
  const banMember = useMutationWithAuthRedirect(api.members.ban);
  const unbanMember = useMutationWithAuthRedirect(api.members.unban);
  const leaveOrg = useMutationWithAuthRedirect(api.members.leave);

  const [messageInput, setMessageInput] = useState("");
  const [playerLinkCode, setPlayerLinkCode] = useState("");
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<{
    messageId: Id<"messages">;
    authorUsername: string;
    content: string;
  } | null>(null);
  const [copiedIp, setCopiedIp] = useState(false);
  const [joining, setJoining] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState<Id<"messages"> | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: "kick" | "ban" | "leave" | "unban";
    memberId?: Id<"members">;
    username?: string;
  } | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top when new messages arrive (newest-first feed)
  useEffect(() => {
    messagesScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !org) return;

    try {
      await sendMessage({
        organizationId: org._id,
        content: messageInput.trim(),
        replyToMessageId: replyTo?.messageId,
      });
      setMessageInput("");
      setReplyTo(null);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleJoin = async () => {
    if (!org) return;
    setJoining(true);
    try {
      await joinOrg({ organizationId: org._id });
    } catch (error) {
      console.error("Failed to join:", error);
    } finally {
      setJoining(false);
    }
  };

  const copyServerIp = async () => {
    if (!org?.serverIp) return;
    const address = org.serverPort
      ? `${org.serverIp}:${org.serverPort}`
      : org.serverIp;
    await navigator.clipboard.writeText(address);
    setCopiedIp(true);
    setTimeout(() => setCopiedIp(false), 2000);
  };

  const handleConfirmDeleteMessage = useCallback(async () => {
    if (!deleteMessageId) return;
    try {
      await removeMessage({ messageId: deleteMessageId });
    } catch (error) {
      console.error("Failed to delete message:", error);
    } finally {
      setDeleteMessageId(null);
    }
  }, [deleteMessageId, removeMessage]);

  const handleConfirmMemberAction = useCallback(async () => {
    if (!confirmAction) return;
    try {
      switch (confirmAction.type) {
        case "kick":
          if (confirmAction.memberId) {
            await kickMember({ memberId: confirmAction.memberId });
          }
          break;
        case "ban":
          if (confirmAction.memberId) {
            await banMember({ memberId: confirmAction.memberId });
          }
          break;
        case "unban":
          if (confirmAction.memberId) {
            await unbanMember({ memberId: confirmAction.memberId });
          }
          break;
        case "leave":
          if (org) {
            await leaveOrg({ organizationId: org._id });
          }
          break;
      }
    } catch (error) {
      console.error(`Failed to ${confirmAction.type}:`, error);
    } finally {
      setConfirmAction(null);
    }
  }, [confirmAction, kickMember, banMember, unbanMember, leaveOrg, org]);

  const handleChangeRole = useCallback(
    async (memberId: Id<"members">, role: "admin" | "moderator" | "member") => {
      try {
        await updateRole({ memberId, role });
      } catch (error) {
        console.error("Failed to change role:", error);
      }
    },
    [updateRole]
  );

  if (org === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 px-4 py-8">
          <div className="mx-auto max-w-6xl">
            <Skeleton className="h-48 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (org === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardContent className="py-12 text-center">
              <h2 className="text-xl font-semibold">Organization Not Found</h2>
              <p className="mt-2 text-muted">
                This organization doesn&apos;t exist or has been deleted.
              </p>
              <Link href="/servers" className="mt-6 inline-block">
                <Button>Browse Servers</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const isMember = membership && membership.status === "active";
  const isAdmin =
    membership &&
    ["owner", "admin"].includes(membership.role);
  const canModerate =
    membership &&
    ["owner", "admin", "moderator"].includes(membership.role);
  const serverAddress = org.serverIp
    ? org.serverPort
      ? `${org.serverIp}:${org.serverPort}`
      : org.serverIp
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Back to explore — always visible so users can return from any org page */}
        <div className="mx-auto max-w-6xl px-4 pt-4 sm:px-6 lg:px-8">
          <Link
            href="/servers"
            className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Server Explorer
          </Link>
        </div>

        <OrganizationHeader
          name={org.name}
          description={org.description}
          memberCount={org.memberCount}
          onlineCount={onlineCount ?? org.onlineCount}
          slug={org.slug}
          serverName={org.serverName}
          serverAddress={serverAddress}
          isMember={Boolean(isMember)}
          isAdmin={Boolean(isAdmin)}
          copiedIp={copiedIp}
          joining={joining}
          onJoin={handleJoin}
          onCopyServerIp={copyServerIp}
        />

        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 sm:p-6 lg:flex-row lg:p-8">
          {/* Chat Section */}
          <div className="flex flex-1 flex-col">
            <Card className="flex flex-1 flex-col">
              <CardHeader className="border-b border-border">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ChatIcon className="h-5 w-5" />
                  Chat
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col p-0">
                {/* Input */}
                {isMember ? (
                  <div className="border-b border-border p-4">
                    {replyTo && (
                      <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-border bg-foreground/5 px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground">
                            Replying to {replyTo.authorUsername}
                          </p>
                          <p className="truncate text-xs text-muted">
                            {replyTo.content}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => setReplyTo(null)}
                          aria-label="Cancel reply"
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                      />
                      <Button type="submit" disabled={!messageInput.trim()}>
                        <SendIcon className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                ) : (
                  <div className="border-b border-border p-4 text-center text-sm text-muted">
                    Join to participate in chat
                  </div>
                )}

                {/* Messages */}
                <div
                  ref={messagesScrollRef}
                  className="flex-1 overflow-y-auto p-4"
                  style={{ maxHeight: "500px" }}
                >
                  {messages === undefined ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-12" />
                      ))}
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-muted">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <MessageBubble
                          key={message._id}
                          message={message}
                          currentUserId={currentUser?._id}
                          canDelete={Boolean(
                            currentUser?._id &&
                              (canModerate || message.author?.id === currentUser._id)
                          )}
                          onDelete={(messageId) => {
                            setDeleteMessageId(messageId);
                          }}
                          onReply={
                            isMember
                              ? (m) => {
                                  setReplyTo({
                                    messageId: m._id,
                                    authorUsername: m.author?.username || "anonymous",
                                    content: m.content,
                                  });
                                }
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Members Sidebar */}
          <div className="w-full lg:w-72">
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UsersIcon className="h-5 w-5" />
                  Members ({org.memberCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  {members === undefined ? (
                    <div className="space-y-2 p-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-10" />
                      ))}
                    </div>
                  ) : (
                    <>
                      {isMember && (
                        <div className="border-b border-border p-4">
                          <p className="text-sm font-medium text-foreground">
                            In-game Account
                          </p>
                          {linkedAccount === undefined ? (
                            <div className="mt-2 space-y-2">
                              <Skeleton className="h-9" />
                              <Skeleton className="h-9" />
                            </div>
                          ) : linkedAccount ? (
                            <p className="mt-1 text-sm text-muted">
                              Linked as{" "}
                              <span className="font-medium text-foreground">
                                {linkedAccount.playerUsername}
                              </span>
                            </p>
                          ) : org.serverId ? (
                            <form
                              className="mt-3 space-y-2"
                              onSubmit={async (e) => {
                                e.preventDefault();
                                if (!org) return;
                                setLinkError(null);
                                setLinkingAccount(true);
                                try {
                                  await linkAccount({
                                    organizationId: org._id,
                                    code: playerLinkCode.trim(),
                                  });
                                  setPlayerLinkCode("");
                                } catch (error) {
                                  setLinkError(
                                    error instanceof Error
                                      ? error.message
                                      : "Failed to link account"
                                  );
                                } finally {
                                  setLinkingAccount(false);
                                }
                              }}
                            >
                              <p className="text-xs text-muted">
                                Run <code>/link</code> in-game to get a 6-digit
                                code.
                              </p>
                              <div className="flex gap-2">
                                <Input
                                  value={playerLinkCode}
                                  onChange={(e) =>
                                    setPlayerLinkCode(e.target.value)
                                  }
                                  placeholder="123456"
                                  className="flex-1"
                                />
                                <Button
                                  type="submit"
                                  disabled={
                                    linkingAccount || !playerLinkCode.trim()
                                  }
                                >
                                  {linkingAccount ? "Linking..." : "Link"}
                                </Button>
                              </div>
                              {linkError && (
                                <p className="text-xs text-destructive">
                                  {linkError}
                                </p>
                              )}
                            </form>
                          ) : (
                            <p className="mt-1 text-xs text-muted">
                              Server not linked yet. Ask an admin to link the
                              server in Organization Settings.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="divide-y divide-border">
                        {members.map((member) => (
                          <MemberRow
                            key={member._id}
                            member={member}
                            manage={
                              canModerate && membership
                                ? {
                                    actorRole: membership.role as MemberManageActions["actorRole"],
                                    onChangeRole: handleChangeRole,
                                    onKick: (memberId, username) =>
                                      setConfirmAction({
                                        type: "kick",
                                        memberId,
                                        username,
                                      }),
                                    onBan: (memberId, username) =>
                                      setConfirmAction({
                                        type: "ban",
                                        memberId,
                                        username,
                                      }),
                                  }
                                : undefined
                            }
                          />
                        ))}
                      </div>

                      {/* Leave Organization button */}
                      {isMember && membership?.role !== "owner" && (
                        <div className="border-t border-border p-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() =>
                              setConfirmAction({ type: "leave" })
                            }
                          >
                            Leave Organization
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Banned Members Section (admin/owner only) */}
            {isAdmin && bannedMembers && bannedMembers.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="border-b border-border">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BanIcon className="h-5 w-5 text-destructive" />
                    Banned ({bannedMembers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[300px] divide-y divide-border overflow-y-auto">
                    {bannedMembers.map((member) => (
                      <BannedMemberRow
                        key={member._id}
                        member={member}
                        onUnban={(memberId) =>
                          setConfirmAction({
                            type: "unban",
                            memberId,
                            username: member.user?.username,
                          })
                        }
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <ConfirmDialog
        open={deleteMessageId !== null}
        onConfirm={handleConfirmDeleteMessage}
        onCancel={() => setDeleteMessageId(null)}
        title="Delete message"
        description="Are you sure you want to delete this message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />

      <ConfirmDialog
        open={confirmAction !== null}
        onConfirm={handleConfirmMemberAction}
        onCancel={() => setConfirmAction(null)}
        title={
          confirmAction?.type === "kick"
            ? "Kick member"
            : confirmAction?.type === "ban"
              ? "Ban member"
              : confirmAction?.type === "unban"
                ? "Unban member"
                : "Leave organization"
        }
        description={
          confirmAction?.type === "kick"
            ? `Are you sure you want to kick ${confirmAction.username ?? "this member"}? They can rejoin later.`
            : confirmAction?.type === "ban"
              ? `Are you sure you want to ban ${confirmAction.username ?? "this member"}? They will not be able to rejoin until unbanned.`
              : confirmAction?.type === "unban"
                ? `Are you sure you want to unban ${confirmAction.username ?? "this member"}? They will be able to rejoin.`
                : "Are you sure you want to leave this organization?"
        }
        confirmText={
          confirmAction?.type === "kick"
            ? "Kick"
            : confirmAction?.type === "ban"
              ? "Ban"
              : confirmAction?.type === "unban"
                ? "Unban"
                : "Leave"
        }
        cancelText="Cancel"
        variant={confirmAction?.type === "unban" ? "default" : "destructive"}
      />

      <Footer />
    </div>
  );
}
